import { zodResolver } from "@hookform/resolvers/zod"
import { memo, useMemo, useState } from "react"
import type { Resolver } from "react-hook-form"
import { FaEdit } from "react-icons/fa"

import { useUpdateClassSession } from "@/hooks/api/useClassSessions"
import { usePermissions } from "@/hooks/usePermissions"
import type {
  ClassSessionUpdateInput,
  EditClassSessionProps,
} from "@/types/class_session"
import { class_sessionUpdateSchema } from "@/types/class_session"
import { Button } from "@chakra-ui/react"

import ClassSessionFormDialog from "./ClassSessionFormDialog"

const EditClassSession = memo(
  ({ class_session, onSuccess, onCancel }: EditClassSessionProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const updateClassSessionMutation = useUpdateClassSession()
    const { hasPermission } = usePermissions()

    const defaultValues = useMemo<ClassSessionUpdateInput>(
      () => ({
        class_template_id: class_session.class_template_id ?? "",
        instructor_id: class_session.instructor_id ?? "",
        session_date: class_session.session_date ?? "",
        session_start: class_session.session_start ?? "",
        session_end: class_session.session_end ?? "",
        status: class_session.status ?? null,
        cancellation_reason: class_session.cancellation_reason ?? "",
        location_override: class_session.location_override ?? "",
        max_students_override: class_session.max_students_override ?? null,
        notes: class_session.notes ?? "",
      }),
      [class_session],
    )

    const handleClose = () => {
      setIsOpen(false)
      onCancel?.()
    }

    const handleSubmit = async (data: ClassSessionUpdateInput) => {
      await updateClassSessionMutation.mutateAsync({
        class_sessionId: class_session.id,
        class_sessionData: data,
      })
      onSuccess?.()
    }

    // Don't render if user doesn't have permission
    if (!hasPermission("class_sessions:update")) {
      return null
    }

    return (
      <>
        <Button
          onClick={() => setIsOpen(true)}
          variant="ghost"
          size="sm"
          aria-label={`Edit classSession: ${class_session.id}`}
        >
          <FaEdit fontSize="14px" aria-hidden="true" />
          Edit
        </Button>

        <ClassSessionFormDialog
          isOpen={isOpen}
          title="Edit Class Meeting"
          submitLabel="Update ClassSession"
          /* TODO: zodResolver generic mismatch with react-hook-form Resolver type */
          resolver={zodResolver(class_sessionUpdateSchema) as Resolver<ClassSessionUpdateInput>}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onClose={handleClose}
          isSubmitting={updateClassSessionMutation.isPending}
          loadingText="Updating..."
        />
      </>
    )
  },
)

EditClassSession.displayName = "EditClassSession"

export default EditClassSession
export type { EditClassSessionProps }
